import { NO_ANALYTICS, battleMode } from "@highmarch/app";
import type { Analytics, AnalyticsEvent } from "@highmarch/app";
import { LevelPhase, starCount } from "@highmarch/domain";
import type { BattleEnd, Feature } from "./registry";
import { openAnalytics } from "../infra/analytics/openAnalytics";
import { isInstalledWebApp } from "../infra/platform/shell";

/** Errors reported per session: enough to see a crash, never a flood from one broken frame loop. */
export const MaxErrorsPerSession = 3;
const MsPerSecond = 1000;

/**
 * Anonymous play statistics (ADR 0015) for the Cloudflare Pages builds: a `session` event at boot, a `battle` event
 * as each battle ends and the first uncaught `error`s. Nothing is read back; local builds and the iOS app send nothing
 * (infra/analytics/openAnalytics.ts).
 */
export const analyticsFeature: Feature = {
  id: "analytics",
  register(ctx, registry) {
    const { analytics, install } = openAnalytics();
    if (analytics === NO_ANALYTICS) return;
    analytics.track({
      name: "session",
      label: new URLSearchParams(location.search).get("utm_source") ?? "",
      numbers: { sessions: install.sessions, days: install.days, installed: isInstalledWebApp() ? 1 : 0, highest: ctx.catalog.highestLevelCleared },
    });
    registry.battleEnd((b) => analytics.track(battleEvent(b, battleMode(ctx.catalog, b.sim.levelNumber, b.sim.modifiers, b.sim.firstClear), ctx.catalog.highestLevelCleared)));
    reportErrors(analytics);
  },
};

/** A battle as it ended: its mode, level, result, how far and how long it ran, its stars and the player's best level. */
export function battleEvent(b: BattleEnd, mode: string, highest: number): AnalyticsEvent {
  const won = b.sim.phase === LevelPhase.Won;
  return {
    name: "battle" as const,
    label: mode,
    numbers: { level: b.sim.levelNumber, won: won ? 1 : 0, stage: b.sim.stage, stages: b.sim.stages, seconds: Math.round(b.sim.clearMs / MsPerSecond), stars: starCount(b.stars ?? 0), highest },
  };
}

function reportErrors(analytics: Analytics): void {
  let left = MaxErrorsPerSession;
  const report = (text: string) => { if (left-- > 0) analytics.track({ name: "error", label: text }); };
  addEventListener("error", (e) => report(`${e.message} @ ${(e.filename ?? "").split("/").pop()}:${e.lineno}`));
  addEventListener("unhandledrejection", (e) => report(`rejection: ${e.reason instanceof Error ? e.reason.message : String(e.reason)}`));
}
